import { useEffect, useMemo, useState } from 'react';
import { ContentCard } from '@/components/layout/PageContainer';
import { StoreCard } from '@/components/common/StoreCard';
import { formatDistance } from '@/lib/distance';
import { getTodayOperatingHours } from '@/lib/operatingHours';
import { restaurantService } from '@/services/restaurant';
import { type RestaurantWithProduct } from '@/types';



interface StoreListProps {
  restaurants: RestaurantWithProduct[];
  selectedOrderType: 'DEADLINE_DISCOUNT' | 'RESERVATION_DISCOUNT' | 'REGULAR_ORDER';
  favoriteStores: Set<string>;
  onStoreCardClick: (storeId: string) => void;
  onFavoriteToggle: (storeId: string) => void;
}

export function StoreList({ 
  restaurants, 
  selectedOrderType, 
  favoriteStores,
  onStoreCardClick,
  onFavoriteToggle
}: StoreListProps) {
  const [deadlineProductsByStore, setDeadlineProductsByStore] = useState<Map<string, any[]>>();

  // 테스트 매장은 노출 제외
  const visibleRestaurants = useMemo(
    () => restaurants.filter(r => (r as any).is_test_store !== true),
    [restaurants]
  );

  // 마감할인 탭일 때만 최소 필드 포함된 데이터 재조회 (제품/오퍼/재고만)
  useEffect(() => {
    let cancelled = false;
    const fetchDeadline = async () => {
      if (selectedOrderType !== 'DEADLINE_DISCOUNT') {
        setDeadlineProductsByStore(undefined);
        return;
      }
      const result = await restaurantService.getDeadlineRestaurantsWithOffers();
      if (!cancelled && result.success && result.data) {
        const map = new Map<string, any[]>();
        result.data.forEach((r: any) => {
          if (r.products && r.products.length > 0) {
            map.set(r.id, r.products);
          }
        });
        setDeadlineProductsByStore(map);
      }
    };
    fetchDeadline();
    return () => { cancelled = true; };
  }, [selectedOrderType]);

  // 마감할인 탭일 때: 기존 매장 정보 유지 + 제품 주입, 제품/재고 없으면 비활성화 처리(노출은 유지)
  const sourceRestaurants = useMemo(() => {
    if (selectedOrderType !== 'DEADLINE_DISCOUNT') return visibleRestaurants;
    if (!deadlineProductsByStore) return visibleRestaurants;

    const enriched = visibleRestaurants.map((r) => {
      const products = deadlineProductsByStore.get(r.id) || [];
      const deadlineStockTotal = products.reduce(
        (sum, p: any) => sum + (p.stock?.[0]?.current_stock || 0),
        0
      );
      const hasDeadlineProducts = products.length > 0 && deadlineStockTotal > 0;

      return {
        ...r,
        products,
        // 마감할인 상품/재고가 없으면 주문불가(회색 처리)
        is_open: hasDeadlineProducts && (r as any).is_open,
        // 표시 순서를 위해 플래그 보관
        __hasDeadlineProducts: hasDeadlineProducts
      } as RestaurantWithProduct & { __hasDeadlineProducts?: boolean };
    });

    // 마감할인 가능 매장을 우선 노출하되 기존 정렬 순서는 유지
    return enriched.sort((a, b) => {
      const aHas = (a as any).__hasDeadlineProducts ? 1 : 0;
      const bHas = (b as any).__hasDeadlineProducts ? 1 : 0;
      if (aHas === bHas) return 0;
      return bHas - aHas; // 가능 매장 우선
    });
  }, [selectedOrderType, visibleRestaurants, deadlineProductsByStore]);

  if (sourceRestaurants.length === 0) {
    return (
      <section className="bg-[#F5F5F5]" style={{ marginTop: '0px', paddingBottom: '80px' }}>
        <div className="flex flex-col items-center w-full" style={{ gap: '16px' }}>
          <ContentCard className="text-center py-12">
            <p className="text-[16px] font-black text-[#333333]">
              판매자 등록이 진행중이에요. 곧 만나요!
            </p>
          </ContentCard>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-[#F5F5F5]" style={{ marginTop: '0px', paddingBottom: '80px' }}>
      <div className="flex flex-col items-center w-full" style={{ gap: '16px' }}>
        {sourceRestaurants.map((restaurant) => {
          // Get today's operating hours using helper function
          const todayHours = getTodayOperatingHours(restaurant as any);
          
          return (
            <StoreCard
              key={restaurant.id}
              id={restaurant.id}
              name={restaurant.name}
              bestmenu={restaurant.bestmenu}
              bestmenu1={restaurant.bestmenu1}
              bestmenu2={restaurant.bestmenu2}
              bestmenu3={restaurant.bestmenu3}
              pickupInstructions={restaurant.pickup_instructions}
              products={restaurant.products?.map((product: any) => ({
                id: product.id,
                name: product.name,
                original_price: product.original_price,
                image_url: product.image_url,
                offers: product.offers || [],
                current_stock: product.stock?.[0]?.current_stock || 0,
                is_available: product.is_available ?? true
              }))}
              isOpen={restaurant.isOrderAvailable ?? restaurant.is_open}
              isNew={restaurant.is_new}
              storeType={restaurant.store_type}
              imageUrl={restaurant.image_url}
              distance={restaurant.distance ? formatDistance(restaurant.distance) : undefined}
              onCardClick={() => onStoreCardClick(restaurant.id)}
              onFavoriteToggle={() => onFavoriteToggle(restaurant.id)}
              isFavorite={favoriteStores.has(restaurant.id)}
              selectedOrderType={selectedOrderType}
              deadlineDiscountStartTime={restaurant.deadline_discount_start_time}
              deadlineDiscountEndTime={restaurant.deadline_discount_end_time}
              todayStart={todayHours.openTime}
              todayEnd={todayHours.closeTime}
              // 예약할인을 위한 필수 데이터 추가
              operating_hours={(restaurant as any).operating_hours}
              reservation_discount_enabled={(restaurant as any).reservation_discount_enabled}
              reservation_available_start={(restaurant as any).reservation_available_start}
              reservation_available_end={(restaurant as any).reservation_available_end}
            />
          );
        })}
      </div>
    </section>
  );
}
